import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Google Analytics 4 integration for Sur Primicia.
 *
 * - Loads gtag.js asynchronously on first mount ONLY when we're on a public page
 *   (skipped when the current pathname starts with `/admin`).
 * - Sends `page_view` events on every route change (SPA-friendly).
 * - Idempotent: safe to mount multiple times; script loads only once.
 * - No impact on paint: script has async attribute and is injected after mount.
 *
 * Measurement ID is read from REACT_APP_GA_MEASUREMENT_ID env var when set;
 * falls back to the hardcoded ID otherwise.
 */
const DEFAULT_MEASUREMENT_ID = "G-QR8R1L83PZ";

function loadGtagScript(measurementId) {
  if (typeof window === "undefined") return;
  if (window.__ga4_loaded) return;
  window.__ga4_loaded = true;

  // Initialize dataLayer + gtag as soon as possible so subsequent
  // gtag() calls before the script has finished loading are queued.
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  // Delay the first automatic page_view — we send it explicitly on route change.
  gtag("config", measurementId, { send_page_view: false });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(s);
}

export default function useGoogleAnalytics(isPublicPage) {
  const location = useLocation();
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID || DEFAULT_MEASUREMENT_ID;

  // Load gtag.js on the first public-page render.
  useEffect(() => {
    if (!isPublicPage) return;
    loadGtagScript(measurementId);
  }, [isPublicPage, measurementId]);

  // Fire page_view on every route change (public pages only).
  useEffect(() => {
    if (!isPublicPage) return;
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const path = location.pathname + (location.search || "");
    // Give the page a beat to update document.title, then send.
    const t = setTimeout(() => {
      window.gtag("event", "page_view", {
        page_location: window.location.href,
        page_path: path,
        page_title: document.title || "Sur Primicia",
        page_referrer: document.referrer || "",
        send_to: measurementId,
      });
    }, 0);
    return () => clearTimeout(t);
  }, [isPublicPage, location.pathname, location.search, measurementId]);
}
